import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { IDV_OVERALL_STATUS, MAX_RETRIES } from 'constants/idv';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import {
  BackRequirementMode,
  VerificationPhase,
  getBackRequirementMode,
  resolveBackRequirement,
} from 'helpers/back-requirement';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import {
  getTranslatedFailureMessage,
  getUnableToVerifyMessage,
  isFaceMismatchFailure,
  shouldAcceptAfterRetries,
} from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { parseIdKey } from 'helpers/ids';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useSubmitProgress } from 'hooks/use-submit-progress';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SelfieModal } from 'views/CandidateApp/CandidateForm/PreSubsectionFlows/components/Selfie/SelfieModal/SelfieModal';

import { SubmitProgressBar } from 'components/SubmitProgressBar/SubmitProgressBar';

import { DOCUMENT_UPLOAD_SLOT_STATE, DocumentUploadSlot } from './DocumentUploadSlot';

const FAILED_STATUSES = [IDV_OVERALL_STATUS.FAILED, IDV_OVERALL_STATUS.REJECTED];

// Prefer the lower (confirmed) count when sides disagree; skip back when FRONT_ONLY.
const reconcileAttempts = (front, back, backIsIrrelevant = false) =>
  backIsIrrelevant ? front : front === back ? front : Math.min(front, back);

export const DocumentDualUpload = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  additionalInstructions = '',
  countryCode = '',
  frontTitle = 'Front',
  backTitle = 'Back',
  uploadCode,
  selfieId,
  selfieIdAtLastVerify = null,
  reTakeSelfie = false,
  showTitleInUploadSlot = true,
  frontValue,
  backValue,
  frontAttempts = 0,
  backAttempts = 0,
  backConditions = null,
  idValues = {},
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder,
  maxSize = 5242880,
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType,
  brand,
  useModel = false,
  disabled = false,
  verifyDocument = false,
  expiryTolerance = null,
  uploadStatus = '',
  uploadError = '',
  faceSimilarity = null,
  faceSimilarityStatus = null,
  hasPhoto = null,
  onChange = () => {},
  onStateChange = () => {},
  handleStateOnVerify = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  updateUpload = async () => {},
  deleteUpload = async () => true,
}) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  const [verifying, setVerifying] = useState(false);
  const { progress: verifyProgress, status: verifyProgressStatus } = useSubmitProgress(verifying);

  // Derived from props each render so sides stay in sync when one changes independently.
  const frontUploadId = useModel ? (frontValue?.id ?? null) : (frontValue ?? null);
  const backUploadId = useModel ? (backValue?.id ?? null) : (backValue ?? null);

  // Source of truth for extracted data; seeded from idValues, updated by live verify.
  const [extractedFields, setExtractedFields] = useState(() =>
    idValues && Object.keys(idValues).length > 0 ? idValues : null,
  );

  // CONDITIONAL: null until front discovery; not sticky — deleting front resets discovery.
  const backRequirementMode = getBackRequirementMode(backConditions);
  const fieldsForBackCheck =
    extractedFields ?? (idValues && Object.keys(idValues).length > 0 ? idValues : null);
  const isBackRequired =
    backRequirementMode === BackRequirementMode.ALWAYS
      ? true
      : fieldsForBackCheck
        ? evaluateFieldConditions(backConditions, fieldsForBackCheck, {})
        : null;
  const { showBackUpload, verificationPhase } = resolveBackRequirement(
    backRequirementMode,
    isBackRequired,
  );
  // Only AWAITING_BOTH needs a back for verify; FRONT_ONLY must not block on a null back.
  const needsBackForVerify = verificationPhase === VerificationPhase.AWAITING_BOTH;
  const isBackMissing = needsBackForVerify && !backUploadId;

  // True from discovery (back required) until the first live joint verify result.
  const [awaitingJointVerify, setAwaitingJointVerify] = useState(
    () =>
      backRequirementMode === BackRequirementMode.CONDITIONAL &&
      isBackRequired === true &&
      !backUploadId,
  );

  // Compare selfieId to metadata stamp so retaken selfies don't reopen the modal on reload.
  const selfieUnchangedSinceFailure =
    selfieIdAtLastVerify == null || selfieId === selfieIdAtLastVerify;

  // Mount-only seed for useState initializers (forceAccept > failed > verified > idle).
  const initialConfirmedAttempts = reconcileAttempts(
    frontAttempts,
    backAttempts,
    verificationPhase === VerificationPhase.FRONT_ONLY,
  );
  const initialOutcome = awaitingJointVerify
    ? 'idle'
    : shouldAcceptAfterRetries(initialConfirmedAttempts, uploadStatus)
      ? 'forceAccepted'
      : FAILED_STATUSES.includes(uploadStatus)
        ? 'failed'
        : uploadStatus
          ? 'verified'
          : 'idle';
  const initialIsFaceMismatch =
    initialOutcome === 'failed' &&
    reTakeSelfie &&
    selfieUnchangedSinceFailure &&
    isFaceMismatchFailure({
      faceSimilarity,
      faceSimilarityStatus,
      hasPhoto,
      attempts: initialConfirmedAttempts,
    });

  // Resolved attempt count; advances only on live verify, never on upload alone.
  const [confirmedAttempts, setConfirmedAttempts] = useState(initialConfirmedAttempts);
  const [outcome, setOutcome] = useState(initialOutcome);
  const [verificationError, setVerificationError] = useState(() =>
    initialOutcome === 'failed'
      ? getTranslatedFailureMessage(uploadError, null, faceSimilarity) || getUnableToVerifyMessage()
      : null,
  );
  const [showSelfieModal, setShowSelfieModal] = useState(initialIsFaceMismatch);

  // IDs from a failed joint submit; both sides must be re-uploaded before re-verify.
  const [staleIds, setStaleIds] = useState(() =>
    initialOutcome === 'failed' ? { front: frontUploadId, back: backUploadId } : null,
  );

  // After a live verify in-session, stop resyncing outcome from props.
  const hasLiveResultRef = useRef(false);

  // Change-handler owns busy; only clear via onUploadingChange(false) if onChange never ran.
  const busyHandledRef = useRef(true);
  // Tracks raw file upload only (separate from `verifying`, which is the verify API call) —
  // exposed via onStateChange so the parent can hide stale extracted fields for the WHOLE
  // replace cycle (upload, then verify), not just the verify portion of it.
  const [isUploading, setIsUploading] = useState(false);

  const beginBusy = useCallback(() => {
    handleStateOnVerify(true);
    disableFormActions();
  }, [handleStateOnVerify, disableFormActions]);

  const endBusy = useCallback(() => {
    handleStateOnVerify(false);
    enableFormActions();
  }, [handleStateOnVerify, enableFormActions]);

  const handleUploadingChange = useCallback(
    isUploadingNow => {
      setIsUploading(isUploadingNow);
      if (isUploadingNow) {
        busyHandledRef.current = false;
        beginBusy();
      } else if (!busyHandledRef.current) {
        endBusy();
      }
    },
    [beginBusy, endBusy],
  );

  useEffect(() => {
    // Skip resync while awaiting joint verify — prospective attempt stamps can falsely agree.
    if (awaitingJointVerify || isBackMissing) return;
    if (verifying || hasLiveResultRef.current) return;
    const attempts = reconcileAttempts(
      frontAttempts,
      backAttempts,
      verificationPhase === VerificationPhase.FRONT_ONLY,
    );
    if (shouldAcceptAfterRetries(attempts, uploadStatus)) {
      setConfirmedAttempts(attempts);
      setOutcome('forceAccepted');
      setVerificationError(null);
      setStaleIds(null);
      setShowSelfieModal(false);
    } else if (FAILED_STATUSES.includes(uploadStatus)) {
      setConfirmedAttempts(attempts);
      setOutcome('failed');
      setVerificationError(
        getTranslatedFailureMessage(uploadError, null, faceSimilarity) ||
          getUnableToVerifyMessage(),
      );
      setStaleIds({ front: frontUploadId, back: backUploadId });
      setShowSelfieModal(
        reTakeSelfie &&
          selfieUnchangedSinceFailure &&
          isFaceMismatchFailure({ faceSimilarity, faceSimilarityStatus, hasPhoto, attempts }),
      );
    } else if (uploadStatus) {
      setConfirmedAttempts(attempts);
      setOutcome('verified');
      setVerificationError(null);
      setStaleIds(null);
      setShowSelfieModal(false);
    }
  }, [
    uploadStatus,
    uploadError,
    faceSimilarity,
    faceSimilarityStatus,
    hasPhoto,
    reTakeSelfie,
    selfieUnchangedSinceFailure,
    frontAttempts,
    backAttempts,
    verifying,
    frontUploadId,
    backUploadId,
    awaitingJointVerify,
    isBackMissing,
  ]);

  // Pure classification of a verify response into the state triggerVerify (and the
  // front-replace-with-existing-back flow below) both need to apply — factored out so neither
  // has to duplicate the force-accept/failed/verified branching.
  const classifyVerifyResult = (data, currentFrontId, currentBackId, nextAttemptNumber) => {
    if (shouldAcceptAfterRetries(nextAttemptNumber, data?.status)) {
      return {
        outcome: 'forceAccepted',
        verificationError: null,
        staleIds: null,
        showSelfieModal: false,
      };
    }
    if (FAILED_STATUSES.includes(data?.status)) {
      const message = getTranslatedFailureMessage(
        data?.reason_for_failure,
        null,
        data?.face_similarity,
      );
      return {
        outcome: 'failed',
        verificationError: message || getUnableToVerifyMessage(),
        staleIds: { front: currentFrontId, back: currentBackId },
        showSelfieModal:
          reTakeSelfie &&
          isFaceMismatchFailure({
            faceSimilarity: data?.face_similarity,
            faceSimilarityStatus: data?.face_similarity_status,
            hasPhoto: data?.has_photo,
            attempts: nextAttemptNumber,
          }),
      };
    }
    return { outcome: 'verified', verificationError: null, staleIds: null, showSelfieModal: false };
  };

  const applyVerifyResult = result => {
    setOutcome(result.outcome);
    setVerificationError(result.verificationError);
    setStaleIds(result.staleIds);
    setShowSelfieModal(result.showSelfieModal);
  };

  // Corrects an EXISTING upload's persisted attempts count (as opposed to the normal
  // stamp-at-create-time path, which only applies to a fresh upload). The backend replaces
  // metadata wholesale on update (no merge), so this always fetches the upload's current
  // metadata first and spreads it, only overriding attempts — never sends a bare
  // {attempts} object, which would silently wipe out dual/id_type/selfie_id_at_verify/etc.
  const syncUploadAttempts = async (uploadId, attempts) => {
    try {
      const current = await getUpload(uploadId);
      await updateUpload({ id: uploadId, metadata: { ...(current?.metadata ?? {}), attempts } });
    } catch (e) {
      console.error('Failed to sync upload attempts:', e);
    }
  };

  const triggerVerify = async (currentFrontId, currentBackId) => {
    if (!currentFrontId) return;
    if (needsBackForVerify && !currentBackId) return;
    if (!verifyDocument || !token) return;
    // Block re-verify until stale sides from a failed attempt are replaced.
    if (
      staleIds &&
      (currentFrontId === staleIds.front || (needsBackForVerify && currentBackId === staleIds.back))
    ) {
      return;
    }

    const idCode = metadata?.id_type || null;
    if (!idCode) return;

    // Front-only while in discovery is a probe, regardless of the response.
    const isDiscoveryCall = verificationPhase === VerificationPhase.FRONT_DISCOVERY;
    const nextAttemptNumber = confirmedAttempts + 1;

    setVerifying(true);
    beginBusy();
    try {
      const { idType: parsedIdType, countryCode } = parseIdKey(idCode);
      const data = await verifyByToken({
        listCode,
        uploadId: currentFrontId,
        backUploadId: needsBackForVerify ? currentBackId : null,
        idCode,
        idType: parsedIdType,
        idCountry: countryCode || null,
        expiryTolerance,
      });

      const newExtractedFields = data?.extracted_fields ?? null;
      setExtractedFields(newExtractedFields);

      if (isDiscoveryCall) {
        const discoveredBackRequired = evaluateFieldConditions(
          backConditions,
          newExtractedFields || {},
          {},
        );
        if (discoveredBackRequired) {
          // Probe only — arm awaitingJointVerify so resync won't treat discovery status as verified.
          setAwaitingJointVerify(true);
          return;
        }
        // Back not needed — this front-only call is attempt 1; fall through.
      }

      hasLiveResultRef.current = true;
      setAwaitingJointVerify(false);
      setConfirmedAttempts(nextAttemptNumber);
      applyVerifyResult(
        classifyVerifyResult(data, currentFrontId, currentBackId, nextAttemptNumber),
      );
    } catch (err) {
      console.error('Error verifying ID document:', err);
      hasLiveResultRef.current = true;
      setAwaitingJointVerify(false);
      setConfirmedAttempts(nextAttemptNumber);
      applyVerifyResult({
        outcome: 'failed',
        verificationError: err?.message || getUnableToVerifyMessage(),
        staleIds: { front: currentFrontId, back: currentBackId },
        showSelfieModal: false,
      });
    } finally {
      setVerifying(false);
    }
  };

  // Replacing the front while a back already exists (CONDITIONAL mode) can't reuse
  // isBackRequired/verificationPhase — those are derived from the OLD front's extracted
  // fields and go stale the instant the front changes. Re-discover from the NEW front first
  // (front-only, ignoring the existing back for this probe). If back is still required,
  // immediately verify jointly with that SAME existing back — no need to make the candidate
  // re-upload it. If not, the existing back is now orphaned relative to this new front —
  // delete it and finalize as front-only. A pure discovery probe never counts as an attempt
  // (matching triggerVerify) — only whichever call actually resolves this (the front-only
  // fallback, or the joint verify) does.
  const handleFrontReplaceWithExistingBack = async (newFrontId, existingBackId) => {
    if (!verifyDocument || !token) return;
    const idCode = metadata?.id_type || null;
    if (!idCode) return;

    const { idType: parsedIdType, countryCode } = parseIdKey(idCode);
    const nextAttemptNumber = confirmedAttempts + 1;
    const verifyArgs = {
      listCode,
      idCode,
      idType: parsedIdType,
      idCountry: countryCode || null,
      expiryTolerance,
    };

    setVerifying(true);
    beginBusy();
    try {
      const discovery = await verifyByToken({
        ...verifyArgs,
        uploadId: newFrontId,
        backUploadId: null,
      });
      const discoveredFields = discovery?.extracted_fields ?? null;
      // evaluateFieldConditions treats a missing field as condition-false — fine when the
      // extraction genuinely succeeded and the field is legitimately absent, but wrong when
      // discovery failed outright or the OCR just didn't capture the field this time (e.g. a
      // blurry photo). Trusting "false" in that case would delete a still-needed back.
      // Fail safe: only conclude "not required" when discovery actually succeeded AND
      // produced some real data; otherwise assume back is still required.
      const discoveryInconclusive =
        FAILED_STATUSES.includes(discovery?.status) ||
        !discoveredFields ||
        Object.keys(discoveredFields).length === 0;
      const backStillRequired =
        discoveryInconclusive ||
        !!evaluateFieldConditions(backConditions, discoveredFields || {}, {});

      if (!backStillRequired) {
        try {
          await deleteUpload(existingBackId);
        } catch (e) {
          console.error('Failed to delete now-unnecessary back upload:', e);
        }
        setExtractedFields(discoveredFields);
        hasLiveResultRef.current = true;
        setAwaitingJointVerify(false);
        // Reset: this is now a front-only document — the old attempts count was accumulated
        // under the dual pairing and isn't meaningful anymore. This discovery result becomes
        // attempt 1 of a fresh front-only sequence (discoveryInconclusive is false here by
        // construction, so this is a genuine success, not a failure to reclassify).
        setConfirmedAttempts(1);
        applyVerifyResult(classifyVerifyResult(discovery, newFrontId, null, 1));
        // The front's metadata.attempts was already stamped at upload time with the OLD
        // (pre-reset) count — correct it to match so it can't disagree with the reset.
        await syncUploadAttempts(newFrontId, 1);
        return;
      }

      // The existing back was already flagged stale by a prior failed joint attempt — don't
      // silently reuse it, that would burn another attempt against a combination already
      // known to include (at least possibly) a bad side. It still needs a genuinely fresh
      // upload (same as the standard "both sides must be replaced" rule) — but rather than
      // leaving it sitting there flagged (which renders as a red "verification failed" box,
      // implying the back document itself was proven bad, when it was never actually shown to
      // be), delete it here. With backUploadId now null, backNeedsReupload naturally reads
      // false, and the slot renders as a plain, empty upload prompt instead.
      if (staleIds?.back === existingBackId) {
        try {
          await deleteUpload(existingBackId);
        } catch (e) {
          console.error('Failed to delete stale back upload:', e);
        }
        setExtractedFields(discoveredFields);
        hasLiveResultRef.current = true;
        setAwaitingJointVerify(true);
        return;
      }

      const joint = await verifyByToken({
        ...verifyArgs,
        uploadId: newFrontId,
        backUploadId: existingBackId,
      });
      setExtractedFields(joint?.extracted_fields ?? null);
      hasLiveResultRef.current = true;
      setAwaitingJointVerify(false);
      setConfirmedAttempts(nextAttemptNumber);
      applyVerifyResult(classifyVerifyResult(joint, newFrontId, existingBackId, nextAttemptNumber));
      // The reused existing back never went through a fresh upload, so its metadata.attempts
      // is still whatever it was from its own last actual upload — sync it to this attempt
      // number so it can't drift from the front's freshly-stamped count.
      await syncUploadAttempts(existingBackId, nextAttemptNumber);
    } catch (err) {
      console.error('Error verifying ID document:', err);
      hasLiveResultRef.current = true;
      setAwaitingJointVerify(false);
      setConfirmedAttempts(nextAttemptNumber);
      applyVerifyResult({
        outcome: 'failed',
        verificationError: err?.message || getUnableToVerifyMessage(),
        staleIds: { front: newFrontId, back: existingBackId },
        showSelfieModal: false,
      });
      await syncUploadAttempts(existingBackId, nextAttemptNumber);
    } finally {
      setVerifying(false);
    }
  };

  // Upload leaves error/stale/outcome alone; deletion clears result and marks the other side stale.
  const handleFrontChange = async newValue => {
    busyHandledRef.current = true;
    try {
      const newFrontId = useModel ? (newValue?.id ?? null) : (newValue ?? null);
      if (newFrontId === null) {
        // Reset attempts only after a resolved result; keep them mid-retry failure recovery.
        const wasResolved = outcome === 'verified' || outcome === 'forceAccepted';
        if (wasResolved) {
          setConfirmedAttempts(0);
        }
        setOutcome('idle');
        setVerificationError(null);
        setExtractedFields(null);

        // CONDITIONAL: front drives back requirement — delete back with front and rediscover.
        if (backRequirementMode === BackRequirementMode.CONDITIONAL && backUploadId) {
          try {
            await deleteUpload(backUploadId);
          } catch (e) {
            console.error('Failed to delete back upload:', e);
          }
          setStaleIds(null);
          onChange({ front: newValue, back: null });
          return;
        }

        // ALWAYS mode: back requirement never changes, so deleting the front doesn't
        // itself invalidate the back. Mirrors handleBackChange's delete branch — don't
        // newly mark the back stale here, or it renders with the misleading "couldn't
        // verify" styling even though it was never actually checked and failed. But if
        // the back was ALREADY stale from an earlier failed joint verify, that flag must
        // survive this delete — otherwise a later front upload would jointly re-verify
        // against a back the both-sides-must-be-replaced rule was blocking, burning
        // another attempt on a pairing that was already known to be invalid.
        setStaleIds(current =>
          current?.back === backUploadId ? { front: null, back: backUploadId } : null,
        );
      }

      onChange({ front: newValue, back: useModel ? backValue : backUploadId });

      // Replacing (not deleting) the front while a back already exists — see
      // handleFrontReplaceWithExistingBack for why this needs its own discovery-first flow
      // instead of reusing the stale isBackRequired/verificationPhase from the old front.
      if (
        newFrontId !== null &&
        backRequirementMode === BackRequirementMode.CONDITIONAL &&
        backUploadId
      ) {
        await handleFrontReplaceWithExistingBack(newFrontId, backUploadId);
        return;
      }

      await triggerVerify(newFrontId, backUploadId);
    } finally {
      endBusy();
    }
  };

  const handleBackChange = async newValue => {
    busyHandledRef.current = true;
    try {
      const newBackId = useModel ? (newValue?.id ?? null) : (newValue ?? null);
      if (newBackId === null) {
        // Reset attempts only after a resolved result; keep them mid-retry failure recovery.
        const wasResolved = outcome === 'verified' || outcome === 'forceAccepted';
        if (wasResolved) {
          setConfirmedAttempts(0);
        }
        setOutcome('idle');
        setVerificationError(null);
        // Unlike handleFrontChange's delete branch, do NOT mark the front as stale here — the
        // back is what determines whether verify can run (needsBackForVerify), not the front,
        // and nothing about the front was invalidated by deleting the back. Forcing it to be
        // replaced too would only be enforceable for the rest of THIS session anyway (staleIds
        // isn't persisted — a reload already re-initializes it to null from the front's own,
        // still-'verified' status, letting the existing front pair with a fresh back with no
        // resubmission required). Leaving staleIds null here makes the live behavior match that,
        // instead of the two diverging depending on whether the candidate happened to reload.
        setStaleIds(null);
        // Do NOT clear extractedFields here — for CONDITIONAL mode, isBackRequired is derived
        // from it, and the front (the only thing that determines whether a back is actually
        // required) hasn't changed. Clearing it would drop back into FRONT_DISCOVERY and hide
        // the back slot entirely until an unnecessary front-only re-verify rediscovers a fact
        // we already know. handleFrontChange still clears it on a front change/delete, which is
        // the only event that can actually invalidate this.
      }
      onChange({ front: useModel ? frontValue : frontUploadId, back: newValue });
      await triggerVerify(frontUploadId, newBackId);
    } finally {
      endBusy();
    }
  };

  // Selfie retake only; docs must still be re-uploaded. No full app refetch (wipes formValues).
  const uploadSelfie = useCallback(
    async photo => {
      await selfieHelper({ photo, candidate: { id: uploadCode }, signUpload, createUpload });
      if (selfieId) {
        await deleteUpload(selfieId);
      }
      setShowSelfieModal(false);
    },
    [uploadCode, signUpload, createUpload, selfieId, deleteUpload],
  );

  // Notify parent of verify state; omit onStateChange from deps (stable setState expected).
  // backUploadId is included so the parent can remount ExtractedFieldsDisplay on a back
  // replacement too, not just a front one — a re-verify can change extracted fields (e.g. the
  // expiry date) even when the front side never changed. busy (uploading OR verifying) lets
  // the parent hide the extracted-fields panel for the whole replace cycle — outcome alone
  // doesn't change until a fresh result lands, so without this the OLD result would keep
  // showing throughout the new upload + verify, misleadingly implying it's already current.
  const busy = isUploading || verifying;
  useEffect(() => {
    onStateChange({
      outcome: isBackMissing ? 'idle' : outcome,
      extractedFields,
      frontUploadId,
      backUploadId,
      busy,
    });
  }, [outcome, isBackMissing, extractedFields, frontUploadId, backUploadId, busy]);

  const isForceAccepted = outcome === 'forceAccepted';

  // Per-side stale flag so only the side still matching staleIds prompts re-upload.
  const frontNeedsReupload = !!staleIds && frontUploadId === staleIds.front;
  const backNeedsReupload = !!staleIds && backUploadId === staleIds.back;

  const baseVerification = isForceAccepted
    ? { state: DOCUMENT_UPLOAD_SLOT_STATE.FORCE_ACCEPTED }
    : outcome === 'verified'
      ? { state: DOCUMENT_UPLOAD_SLOT_STATE.VERIFIED }
      : undefined;

  const frontVerification = frontNeedsReupload
    ? {
        state: DOCUMENT_UPLOAD_SLOT_STATE.VERIFY_FAILED_RETRY,
        errorMessage: verificationError,
        attemptCount: confirmedAttempts,
        maxAttempts: MAX_RETRIES,
      }
    : baseVerification;
  const backVerification = backNeedsReupload
    ? {
        state: DOCUMENT_UPLOAD_SLOT_STATE.VERIFY_FAILED_RETRY,
        errorMessage: verificationError,
        attemptCount: confirmedAttempts,
        maxAttempts: MAX_RETRIES,
      }
    : baseVerification;
  // Next attempt stamped on both sides at create time so they can't diverge.
  const nextAttemptNumber = confirmedAttempts + 1;

  // Stamp selfie + list code for reload/list scoping while verify is still in flight.
  const sideMetadata = {
    ...metadata,
    attempts: nextAttemptNumber,
    selfie_id_at_verify: selfieId ?? null,
    ...(listCode ? { id_requirements_list: listCode } : {}),
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <SelfieModal
        show={showSelfieModal}
        brand={brand}
        screenName="camera"
        noClose
        title="Retake selfie"
        description="We ran into a little issue verifying your ID with the photo you provided. Could you take a clearer picture for us? Make sure your face is fully visible and well-lit."
        onSave={photo => uploadSelfie(photo)}
      />

      <DocumentUploadSlot
        side="front"
        heading={`${documentTitle} - ${frontTitle}`}
        brand={brand}
        disabled={disabled || verifying || isForceAccepted}
        value={frontValue}
        useModel={useModel}
        description={documentDescription}
        additionalInstructions={additionalInstructions}
        countryCode={countryCode}
        modelParams={modelParams}
        metadata={{ ...sideMetadata, dual: 'front' }}
        bucketFolder={bucketFolder}
        maxSize={maxSize}
        accept={effectiveAccept}
        acceptLabel={allowedLabel}
        isFileAllowed={isFileAllowed}
        getUpload={getUpload}
        signUpload={signUpload}
        createUpload={createUpload}
        deleteUpload={deleteUpload}
        verification={frontVerification}
        showTitle={showTitleInUploadSlot}
        onChange={handleFrontChange}
        onUploadingChange={handleUploadingChange}
      />

      {showBackUpload && (
        <DocumentUploadSlot
          side="back"
          heading={`${documentTitle} - ${backTitle}`}
          brand={brand}
          disabled={disabled || verifying || isForceAccepted}
          value={backValue}
          useModel={useModel}
          modelParams={modelParams}
          metadata={{ ...sideMetadata, dual: 'back' }}
          bucketFolder={bucketFolder}
          maxSize={maxSize}
          accept={effectiveAccept}
          acceptLabel={allowedLabel}
          isFileAllowed={isFileAllowed}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
          verification={backVerification}
          showTitle={showTitleInUploadSlot}
          onChange={handleBackChange}
          onUploadingChange={handleUploadingChange}
        />
      )}

      <SubmitProgressBar
        progress={verifyProgress}
        status={verifyProgressStatus}
        loadingLabel={t('components.documentUploadSlot.verifying')}
      />
    </div>
  );
};

DocumentDualUpload.propTypes = {
  documentTitle: PropTypes.string,
  frontTitle: PropTypes.string,
  backTitle: PropTypes.string,
  uploadCode: PropTypes.any,
  selfieId: PropTypes.number,
  selfieIdAtLastVerify: PropTypes.number,
  reTakeSelfie: PropTypes.bool,
  frontValue: PropTypes.any,
  backValue: PropTypes.any,
  frontAttempts: PropTypes.number,
  backAttempts: PropTypes.number,
  backConditions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  idValues: PropTypes.object,
  modelParams: PropTypes.object,
  listCode: PropTypes.string,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  disabled: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  expiryTolerance: PropTypes.number,
  uploadStatus: PropTypes.string,
  uploadError: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  faceSimilarity: PropTypes.number,
  faceSimilarityStatus: PropTypes.string,
  hasPhoto: PropTypes.bool,
  onChange: PropTypes.func,
  onStateChange: PropTypes.func,
  handleStateOnVerify: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  updateUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
};
